import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid2,
  IconButton,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import InlineFormError from "../../../../../../views/components/common/form/InlineFormError";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import {
  OrderClientPO,
  validateClientPOFile,
} from "../../../../../../models/Order/OrderClientPO";
import { OrderClientPOStore } from "../../../../stores/OrderClientPOStore";
import ClientPOFilePicker from "./ClientPOFilePicker";
import { formatClientPODate, saveClientPOFile } from "./clientPOFile";

interface ClientPORowProps {
  po: OrderClientPO;
  index: number;
  store: OrderClientPOStore;
  onRemove: (poId: number) => void;
}

const ClientPORow: React.FC<ClientPORowProps> = observer(
  ({ po, index, store, onRemove }) => {
    const theme = useTheme();
    const colors = getModernPageColors(theme);
    const isEditing = store.editingId === po.id;
    const isSelected = store.selectedIds.includes(po.id);

    const [draftNumber, setDraftNumber] = useState(po.po_number ?? "");
    const [draftFile, setDraftFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState("");

    useEffect(() => {
      if (isEditing) {
        setDraftNumber(po.po_number ?? "");
        setDraftFile(null);
        setFileError("");
      }
    }, [isEditing, po.po_number]);

    const handleDownload = async () => {
      const response = await store.downloadClientPO(po);
      if (response) saveClientPOFile(response, po);
    };

    const handleFileSelect = (picked: File) => {
      const error = validateClientPOFile(picked);
      setFileError(error);
      setDraftFile(error ? null : picked);
    };

    const handleSave = () => {
      void store.updateClientPO(po.id, draftNumber, draftFile);
    };

    const actor = po.updated_by ?? po.created_by;
    const meta = [
      po.updated_by ? "Edited" : "Added",
      actor ? `by ${actor.name}` : null,
      `· ${formatClientPODate(po.updated_by ? po.updated_at : po.created_at)}`,
      po.platform ? `· ${po.platform}` : null,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Box
        sx={{
          px: { xs: 1.25, sm: 1.5 },
          py: 1.25,
          borderRadius: 2,
          border: `1px solid ${
            isEditing || isSelected ? colors.accentPurple : colors.border
          }`,
          bgcolor: isSelected ? colors.surfaceSoft : colors.surface,
          transition: "border-color 0.15s ease, background-color 0.15s ease",
        }}
      >
        <Grid2 container spacing={1} alignItems="center">
          <Grid2 size="auto">
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => store.toggleSelected(po.id)}
              disabled={store.isBusy || isEditing}
              inputProps={{
                "aria-label": `Select PO ${po.po_number ?? po.id}`,
              }}
              sx={{ p: 0.5 }}
            />
          </Grid2>
          <Grid2 size="auto">
            <CustomTypography
              variant="heading8"
              color={colors.subtitle}
              sx={{ minWidth: 20 }}
            >
              {index + 1}.
            </CustomTypography>
          </Grid2>

          {isEditing ? (
            <Grid2 size="grow">
              <Grid2 container spacing={1}>
                <Grid2 size={{ xs: 12, sm: 5 }}>
                  <CustomTextField
                    width="100%"
                    value={draftNumber}
                    placeholder="PO number"
                    preventEdgeSpaces
                    onSearch={setDraftNumber}
                    error={!draftNumber.trim()}
                    disabled={store.isSavingEdit}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 7 }}>
                  <ClientPOFilePicker
                    file={draftFile}
                    placeholder="Replace PDF (optional)"
                    disabled={store.isSavingEdit}
                    error={!!fileError}
                    onSelect={handleFileSelect}
                    onClear={() => setDraftFile(null)}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          ) : (
            <Grid2 size="grow" sx={{ minWidth: 0 }}>
              <CustomTypography
                variant="heading7"
                color={colors.title}
                sx={{ wordBreak: "break-word" }}
              >
                {po.po_number || "—"}
              </CustomTypography>
              <CustomTypography
                variant="caption"
                color={colors.subtitle}
                sx={{ display: "block", mt: 0.25 }}
              >
                {meta}
              </CustomTypography>
            </Grid2>
          )}

          <Grid2
            size={{ xs: 12, sm: "auto" }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
            }}
          >
            {isEditing ? (
              <>
                <AppButton
                  label="Cancel"
                  variant="transparentPrimary"
                  onClick={store.cancelEdit}
                  disabled={store.isSavingEdit}
                  customStyles={{ height: 34, minHeight: 34, px: 1.5 }}
                />
                <AppButton
                  label={store.isSavingEdit ? "Saving..." : "Save"}
                  onClick={handleSave}
                  disabled={store.isSavingEdit || !draftNumber.trim()}
                  customStyles={{ height: 34, minHeight: 34, px: 1.5 }}
                />
              </>
            ) : (
              <>
                {po.po_url ? (
                  <AppButton
                    label={
                      store.downloadingId === po.id ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        "PDF"
                      )
                    }
                    variant="transparentPrimary"
                    startIcon={<PictureAsPdfOutlinedIcon />}
                    endIcon={<FileDownloadOutlinedIcon />}
                    onClick={() => void handleDownload()}
                    disabled={store.downloadingId === po.id}
                    customStyles={{
                      height: 30,
                      minHeight: 30,
                      px: 1.25,
                      borderRadius: "999px",
                    }}
                  />
                ) : (
                  <CustomTypography
                    variant="caption"
                    color={colors.subtitle}
                    sx={{ px: 1 }}
                  >
                    No PDF
                  </CustomTypography>
                )}
                <IconButton
                  size="small"
                  title="Edit PO"
                  aria-label="Edit PO"
                  onClick={() => store.startEdit(po.id)}
                  disabled={store.isBusy}
                  sx={{ color: colors.accentPurple }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  title="Remove PO"
                  aria-label="Remove PO"
                  onClick={() => onRemove(po.id)}
                  disabled={store.isBusy}
                  sx={{ color: colors.error }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Grid2>
        </Grid2>

        {isEditing && (
          <>
            <InlineFormError message={fileError} sx={{ mb: 0 }} />
            <InlineFormError message={store.editError} sx={{ mb: 0 }} />
          </>
        )}
      </Box>
    );
  },
);

export default ClientPORow;
